import { QueryClient, QueryKey } from "@tanstack/react-query";

import { updateItemInQuery } from "./create-item-updater-for-query";
import {
  createHccItemUpdaterForQueryLists,
  createItemUpdaterForQueryLists,
} from "./create-item-updater-for-query-list";
import { HasDefaultItemId, ItemUpdaterProps } from "./types/update-item-props";
import { defaultGetItemId } from "./utils/default-get-item-id";

type BaseProps<TId extends string | number> = {
  getListQueryKey: (id: TId) => QueryKey;
  getSingleQueryKey: (id: TId) => QueryKey;
};

/**
 * Conditional props:
 * - If TItem has an `id: string` -> getItemId is optional.
 * - Else getItemId is required.
 */
export type CreateUpdateForQueriesProps<TItem, TUpdate extends Partial<TItem>> = TItem extends {
  id: string;
}
  ? BaseProps<string> & { getItemId?: (item: TItem | TUpdate) => string | undefined }
  : BaseProps<string> & { getItemId: (item: TItem | TUpdate) => string | undefined };

/**
 * Conditional props:
 * - If TItem has an `id` of type number (may be optional) -> getItemId is optional.
 * - Else getItemId is required.
 */
type CreateHccUpdateForQueriesProps<TItem, TUpdate extends Partial<TItem>> = BaseProps<number> &
  (HasDefaultItemId<TItem, number> extends true
    ? { getItemId?: (item: TItem | TUpdate) => number | undefined }
    : { getItemId: (item: TItem | TUpdate) => number | undefined });

/**
 * Factory function that creates an updater function for both individual items and list queries.
 * This ensures cache consistency when an item is updated - both the individual item cache
 * and any lists containing that item are updated simultaneously.
 *
 * @param queryClient The QueryClient instance to use for cache updates
 * @param config Configuration object with query key factories and item ID extraction
 * @returns Async function that updates both individual and list query caches
 */

export const createUpdateForQueries = <
  TItem = { id: string },
  TUpdate extends Partial<TItem> = Partial<TItem>
>(
  queryClient: QueryClient,
  {
    getSingleQueryKey,
    getListQueryKey,
    getItemId = defaultGetItemId,
  }: CreateUpdateForQueriesProps<TItem, TUpdate>
) => {
  const updateItemQuery = updateItemInQuery<TItem, TUpdate>(queryClient, {
    getQueryKey: getSingleQueryKey,
    getItemId,
  } as ItemUpdaterProps<TItem, TUpdate>);
  const updateItemInLists = createItemUpdaterForQueryLists<TItem, TUpdate>(queryClient, {
    getQueryKey: getListQueryKey,
    getItemId,
  } as ItemUpdaterProps<TItem, TUpdate>);

  // `item` is null when the item was deleted
  return async (beforeId: string, item: TUpdate | null) => {
    await updateItemQuery(beforeId, item);
    await updateItemInLists(beforeId, item);
  };
};

/**
 * HCC variant of `createUpdateForQueries`: numeric ids and `{ count, results }` lists.
 */
export const createHccUpdateForQueries = <TItem, TUpdate extends Partial<TItem> = Partial<TItem>>(
  queryClient: QueryClient,
  {
    getSingleQueryKey,
    getListQueryKey,
    getItemId = defaultGetItemId,
  }: CreateHccUpdateForQueriesProps<TItem, TUpdate>
) => {
  const updateItemQuery = updateItemInQuery<TItem, TUpdate, number>(queryClient, {
    getQueryKey: getSingleQueryKey,
    getItemId,
  } as ItemUpdaterProps<TItem, TUpdate, number>);
  const updateItemInLists = createHccItemUpdaterForQueryLists<TItem, TUpdate>(queryClient, {
    getQueryKey: getListQueryKey,
    getItemId,
  } as ItemUpdaterProps<TItem, TUpdate, number>);

  // `item` is null when the item was deleted
  return async (beforeId: number, item: TUpdate | null) => {
    await updateItemQuery(beforeId, item);
    await updateItemInLists(beforeId, item);
  };
};
